import { Controller } from "react-hook-form";
import { DropdownItem, Checkbox } from "@components";
import { cn } from "@utils";
import EditorDropdown from "./EditorDropdown";

const invoiceOptions = [
  {
    value: "Overdue",
    label: "Overdue",
    description: "Payment link shows all invoices with overdue pre-selected",
  },
  {
    value: "OverdueAndDueThisMonth",
    label: "Overdue + Due",
    description:
      "Payment link shows all invoices with overdue + due pre-selected",
  },
  {
    value: "All",
    label: "All invoices",
    description: "Payment link shows all invoices pre-selected",
  },
  {
    value: "None",
    label: "No invoices requested",
    description: "Payment link shows all invoices with nothing pre-selected",
  },
  {
    value: "Custom",
    label: "Custom amount",
    description: "Payment link shows a fixed amount",
  },
];

// Specific invoices are chosen upstream (the intelligence flow supplies the invoice numbers), never
// picked by hand — so this is surfaced only when the request is already in that mode, and is not a
// general option on the dropdown for ordinary payment requests.
const specificInvoicesOption = {
  value: "SpecificInvoices",
  label: "Specific invoices",
  description: "Payment link shows the specific invoices requested",
};

export default function InvoiceDropdown({
  control,
  watch,
  setValue,
  variant = "inline",
  edited = false,
  disabled = false,
  disableCustom = false,
}) {
  const invoicesRequested = watch("invoicesRequested");
  const mandatory = watch("mandatory");
  const noInvoices = invoicesRequested === "None";
  const isCustom = invoicesRequested === "Custom";
  const isSpecific = invoicesRequested === "SpecificInvoices";

  // Show "Specific invoices" only while the request is in that mode; it is never a general option.
  const options = isSpecific
    ? [specificInvoicesOption, ...invoiceOptions]
    : invoiceOptions;

  const selectedLabel =
    options.find((o) => o.value === invoicesRequested)?.label ?? "Select…";

  const displayText = mandatory
    ? `${selectedLabel} · Mandatory`
    : selectedLabel;

  return (
    <EditorDropdown
      label={displayText}
      variant={variant}
      edited={edited}
      disabled={disabled}
      bodyClassName="min-w-80"
    >
      <div className="px-4 py-2 text-xs font-semibold text-zinc-500 uppercase tracking-wide">
        Payment request
      </div>
      <Controller
        name="invoicesRequested"
        control={control}
        render={({ field: { value, onChange } }) => (
          <>
            {options.map((opt) => {
              const isDisabled = disableCustom && opt.value === "Custom";
              return (
                <DropdownItem
                  key={opt.value}
                  onClick={() => {
                    if (isDisabled) return;
                    onChange(opt.value);
                    if (opt.value === "None") setValue("mandatory", false);
                    if (opt.value === "Custom") setValue("mandatory", true);
                    setValue("customAmount", "");
                  }}
                  className={cn("!items-start", isDisabled && "opacity-50")}
                >
                  <div className="w-full select-none">
                    <div
                      className={cn(
                        "font-medium",
                        isDisabled
                          ? "text-zinc-400"
                          : opt.value === value
                            ? "text-primary-500"
                            : "text-zinc-900",
                      )}
                    >
                      {opt.label}
                    </div>
                    <div
                      className={cn(
                        "text-xs mt-0.5",
                        isDisabled ? "text-zinc-400" : "text-zinc-500",
                      )}
                    >
                      {isDisabled
                        ? "Only available for single customer"
                        : opt.description}
                    </div>
                  </div>
                </DropdownItem>
              );
            })}
          </>
        )}
      />

      <div className="border-t border-zinc-200 mx-1 my-1" />

      <Controller
        name="mandatory"
        control={control}
        render={({ field: { value, onChange } }) => (
          <div
            role="button"
            onClick={() => !noInvoices && !isCustom && onChange(!value)}
            className={cn(
              "flex items-center rounded px-4 py-2 text-sm",
              noInvoices || isCustom
                ? "text-zinc-400 cursor-default"
                : "text-zinc-700 hover:bg-zinc-100 hover:text-zinc-900 cursor-pointer",
            )}
          >
            <Checkbox
              readonly
              disabled={noInvoices || isCustom}
              style={{ pointerEvents: "none" }}
              checked={value}
              className="relative left-0 top-0 -mt-0"
            />
            <span className="ml-5 select-none">
              Make selected invoices mandatory
            </span>
          </div>
        )}
      />
    </EditorDropdown>
  );
}
